import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Box, Button, IconButton, Chip, Tab, Tabs, Tooltip } from "@mui/material";
import { Add, InfoOutlined, Print, QrCodeScanner } from "@mui/icons-material";
import { useSelector } from "react-redux";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import { useTablePagination, useAppMeta } from "hooks";
import { useLazyGetBranchTallyListQuery } from "store/services/branchTallyApi";
import BranchTallyFilter from "./BranchTallyComponents/BranchTallyFilter";
import { useCustomParams } from "hooks/useCustomParams";

const StatusChip = ({ status }) => {
  const map = {
    Completed: { label: "Completed", color: "success" },
    InProgress: { label: "In Progress", color: "warning" },
  };
  const cfg = map[status] || map.InProgress;
  return <Chip label={cfg.label} color={cfg.color} size="small" />;
};

// Only visible to actual participants; blocks everyone else from joining a scan
const ScanActionButton = ({ row, currentUserId }) => {
  const navigate = useNavigate();
  const participant = row?.participants?.find(
    (p) => p._id?.toString() === currentUserId?.toString(),
  );
  const isParticipant = !!participant;
  const isInProgress = row?.status === "InProgress";
  const hasCompleted = !!participant?.completedAt;

  if (!isInProgress || !isParticipant) return null;

  if (hasCompleted) {
    return (
      <Tooltip title="You have completed this tally" arrow>
        <span>
          <IconButton size="small" color="success" disabled>
            <QrCodeScanner />
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip title="Continue Scanning" arrow>
      <span>
        <IconButton
          size="small"
          color="warning"
          onClick={() => navigate(`/app/crm/branch-tally/scan/${row._id}`)}
        >
          <QrCodeScanner />
        </IconButton>
      </span>
    </Tooltip>
  );
};

const BranchTallyListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();
  const { params } = useCustomParams();
  const { fromDate = "", toDate = "", branch = "" } = params;
  const currentUserId = useSelector((state) => state.auth.user?._id);
  const [scope, setScope] = useState("mine");

  const breadcrumbs = [{ title: "Branch Tally" }];

  const [fetchList, { data: list, isLoading, isFetching, error }] =
    useLazyGetBranchTallyListQuery();

  useEffect(() => {
    fetchList({
      page: currentPage,
      limit: perPage,
      fromDate,
      toDate,
      branch,
      scope,
    });
  }, [fetchList, currentPage, perPage, fromDate, toDate, branch, scope]);

  const handleTabChange = (_, value) => {
    setScope(value);
    handlePageChange(1);
  };

  const columns = [
    { name: "Branch", selector: (row) => row?.branch?.name, sortable: true },
    {
      name: "Status",
      selector: (row) => row?.status,
      cell: (row) => <StatusChip status={row?.status} />,
    },
    {
      name: "Verification",
      selector: (row) => (row?.approvedBy ? "Verified" : "Unverified"),
      cell: (row) =>
        row?.status === "Completed" ? (
          <Chip
            label={row?.approvedBy ? "Verified" : "Unverified"}
            color={row?.approvedBy ? "success" : "default"}
            size="small"
            variant={row?.approvedBy ? "filled" : "outlined"}
          />
        ) : (
          <span style={{ color: "#94a3b8", fontSize: 12 }}>—</span>
        ),
    },
    {
      name: "Started At",
      selector: (row) => row?.startedAt,
      cell: (row) => <AppTableDateTime date={row?.startedAt} />,
      sortable: true,
    },
    { name: "Tallied By", selector: (row) => row?.participants?.length },
    { name: "Tallied", selector: (row) => row?.talliedCount },
    { name: "Missing Physical", selector: (row) => row?.missingPhysicalCount },
    { name: "Missing System", selector: (row) => row?.missingSystemCount },
    {
      name: "Actions",
      cell: (row) => (
        <div className="flex items-center gap-1">
          {row?.status === "Completed" && (
            <Link to={`/app/crm/branch-tally/${row?._id}/detail`}>
              <Tooltip title="View Detail" arrow>
                <IconButton size="small" color="primary">
                  <InfoOutlined />
                </IconButton>
              </Tooltip>
            </Link>
          )}
          {row?.status === "Completed" && (
            <Link
              to={`/app/crm/branch-tally/print/${row._id}`}
              state={{ record: row }}
            >
              <Tooltip title="Print Report" arrow>
                <IconButton size="small" color="primary">
                  <Print />
                </IconButton>
              </Tooltip>
            </Link>
          )}
          {row?.status === "InProgress" && (
            <ScanActionButton row={row} currentUserId={currentUserId} />
          )}
        </div>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  useAppMeta("Branch Tally");

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={scope}
            onChange={handleTabChange}
            aria-label="branch tally scope"
          >
            <Tab label="My Tallies" value="mine" />
            <Tab label="All Tallies" value="all" />
          </Tabs>
        </Box>
        <BranchTallyFilter />
        <AppTable
          columns={columns}
          data={list?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={list?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          conditionalRowStyles={[
            { when: (row) => row?.isOnHold, style: { background: "#fffbeb" } },
          ]}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default BranchTallyListScreen;

const PageActions = () => (
  <Link to="/app/crm/branch-tally/create">
    <Button startIcon={<Add />} variant="contained">
      New Tally
    </Button>
  </Link>
);
